import React, { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { updateHomepageModule } from "../../../../../../store/actions/admin/create-event";
import { CreateSpeaker, RelatedSpeakerSessions, Speaker, SpeakerPageModuleContent, TextOverrides, ColorKeys } from "../../../../../../types/working-model";
import ModalComponent from "../../../../../general-ui/modal/modal";
import { updateSpeaker } from "../../../../../../store/actions/admin/speakers";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
} from '@dnd-kit/sortable';
import SpeakerComponent from "./speaker";
import { restrictToParentElement } from '@dnd-kit/modifiers';
import CreateSpeakerModal from "../../../../content-creation/speakers/speaker";
import { getTextValue, updateTranslateKey } from "../../../../../../utils/utils";
import classNames from "classnames";
import { SpeakerLayoutTypesBold, SpeakerLayoutTypesClassic, SpeakerLayoutTypesFun, TemplateClassNames } from "../../../../../../types/template-layouts";
import Controls from "../../../session/editor/moduleControls/controls";
import { EContentColorOverrideNames } from "../../../session/editor/moduleControls/edit-section-colors-modal";
import { getStylingOverrides } from "../../../../../live-event/utils";

import '../../../../../live-event/modules/speakers/speaker-layout.scss';
import './speakers.scss';
import EditableTextSlim from "../../../../../general-ui/editable-text/editable-text-slim";
import EditableTextDraft from "../../../../../general-ui/editable-text/editable-text-draft";
import { Button } from "../../../../../general-ui/button/button";
import { EIcon } from "../../../../../general-ui/icon/icon";
import LargeButton from "../../../../../general-ui/button/large-button";
import { BasicLangingModuleProps } from "../editor-main";

const EditorSpeakers: React.FC<BasicLangingModuleProps> = React.forwardRef<HTMLDivElement, BasicLangingModuleProps>(({ page_module, template, language, baseLanguage }, ref) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [replacing, setReplacing] = useState<Speaker | null>(null);
	const [workingSpeaker, setWorkingSpeaker] = useState<CreateSpeaker | Speaker | null>(null);
	const [confirmingDelete, setConfirmingDelete] = useState<boolean>(false);
	const [speakerToRemove, setSpeakerToRemove] = useState<Speaker | null>(null);
	const [showMore, setShowMore] = useState(false);

	const content: SpeakerPageModuleContent = page_module?.content;

	const layout_type = content?.layout_type || "standard";
	const styling_overrides = page_module.styling_overrides;
	const stylingOverrides = getStylingOverrides(styling_overrides);

	const dispatch = useDispatch();


	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	function handleTextChange(key: string) {
		return (text: string) => {
			dispatch(
				updateHomepageModule({
					...page_module,
					content: {
						...content,
						[key]: updateTranslateKey({
							translateString: page_module?.content?.[key],
							input: text,
							language,
							baseLanguage
						}),
					},
				})
			);
		};
	}

	function handleStyleOverrideChange(key: string) {
		return (overrides: TextOverrides) => {
			const updatedOverrides = {
				...styling_overrides,
				content: {
					...styling_overrides?.content,
					[key]: overrides
				}
			};
			dispatch(
				updateHomepageModule({
					...page_module,
					styling_overrides: updatedOverrides
				})
			);
		};
	}

	function closeModal() {
		setModalOpen(false);
		setTimeout(() => setWorkingSpeaker(null), 400);
	}

	function openModal() {
		setReplacing(null);
		setWorkingSpeaker(null);
		setModalOpen(true);
	}

	function replaceSpeaker(s: Speaker) {
		setWorkingSpeaker(null);
		setReplacing(s);
		setModalOpen(true);
	}

	function updatePageModule(speakerList: Speaker[]) {
		dispatch(updateHomepageModule({
			...page_module,
			modules: speakerList,
			content_modules: speakerList.map((s: Speaker) => s.speaker)
		}));

	}

	function onSpeakerComplete(speaker: Speaker, relatedSessions: number[]) {
		const updatedModules = page_module.modules?.map((module: Speaker) => {
			if (module.speaker === (speaker as Speaker).speaker) {
				return speaker;
			} else {
				return module;
			}
		});
		const _relatedSessions = getRelatedSessionsComplete(relatedSessions, speaker.speaker!);

		if ((workingSpeaker as Speaker)?.speaker) {
			dispatch(updateSpeaker(speaker as Speaker));
			dispatch(
				updateHomepageModule(
					{
						...page_module,
						modules: updatedModules,
						content: { ...page_module.content, related_sessions: _relatedSessions }
					}
				)
			);
		} else {
			dispatch(
				updateHomepageModule(
					{
						...page_module,
						content_modules: [...(page_module?.content_modules ?? []), speaker.speaker],
						modules: [...(page_module?.modules ?? []), speaker],
						content: { ...page_module.content, related_sessions: _relatedSessions }
					}
				)
			);
		}

		setModalOpen(false);
	}

	function editSpeaker(speaker: Speaker) {
		return () => {
			setWorkingSpeaker(speaker);
			setModalOpen(true);
		};
	}

	function closeDeleteModal() {
		setConfirmingDelete(false);
		setSpeakerToRemove(null);
	}

	function checkDeleteSpeaker(speaker: Speaker) {
		return () => {
			setSpeakerToRemove(speaker);
			setConfirmingDelete(true);
		};
	}

	function removeSpeaker() {
		if (speakerToRemove && page_module.modules) {
			dispatch(
				updateHomepageModule(
					{
						...page_module,
						modules: page_module.modules.filter((module: Speaker) => module.speaker !== speakerToRemove.speaker),
						content_modules: page_module.content_modules?.filter((module: number) => module !== speakerToRemove.speaker) ?? []
					}
				)
			);
		}

		closeDeleteModal();
	}

	function getRelatedSessionsComplete(relatedSessions: number[], speaker: number) {
		if (relatedSessions && speaker) {

			let relatedSessionExists = false;
			const _relatedSessions = page_module.content?.related_sessions?.map((r: RelatedSpeakerSessions) => {
				if (speaker === r.speaker) {
					relatedSessionExists = true;
					return {
						speaker,
						sessions: relatedSessions
					};
				}
				return r;
			});
			// if speaker doesnt already have related_sessions
			if (!relatedSessionExists) {
				_relatedSessions?.push({
					speaker,
					sessions: relatedSessions
				});
			}

			return _relatedSessions;
		}
		return null;
	}

	function handleDragEnd(event: any) {
		const _modules = [...page_module.modules!];

		const startIndex = _modules.findIndex((speaker: Speaker) => Number(speaker.speaker) === Number(event.active.id));
		const newIndex = _modules.findIndex((speaker: Speaker) => Number(speaker.speaker) === Number(event.over.id));

		const [module] = _modules.splice(startIndex, 1);
		_modules.splice(newIndex, 0, module);

		dispatch(
			updateHomepageModule(
				{
					...page_module,
					modules: _modules,
					content_modules: _modules.map((speaker: Speaker) => speaker.speaker)
				}
			)
		);
	}

	function handleShowMore() {
		setShowMore((prev) => !prev);
	}


	const maxSpeakers = useMemo(() => {
		switch (layout_type) {
			case SpeakerLayoutTypesClassic.standard:
			case SpeakerLayoutTypesBold.standard:
			case SpeakerLayoutTypesFun.standard:
				return 6;

			case SpeakerLayoutTypesClassic.small_horizontal:
			case SpeakerLayoutTypesBold.small_horizontal:
			case SpeakerLayoutTypesFun.small_horizontal:
				return 4;

			case SpeakerLayoutTypesClassic.staggered:
			case SpeakerLayoutTypesBold.staggered:
			case SpeakerLayoutTypesFun.staggered:
				return 2;

			case SpeakerLayoutTypesClassic.long_card:
			case SpeakerLayoutTypesBold.long_card:
			case SpeakerLayoutTypesFun.long_card:
				return 3;

			case SpeakerLayoutTypesClassic.solid:
			case SpeakerLayoutTypesBold.solid:
			case SpeakerLayoutTypesFun.solid:
				return 6;

			case SpeakerLayoutTypesClassic.small_vertical:
			case SpeakerLayoutTypesBold.small_vertical:
			case SpeakerLayoutTypesFun.small_vertical:
				return 4;
			default:
				return 2;
		}
	}, [layout_type]);
	const speakerCount = page_module?.modules?.length ?? maxSpeakers;

	function limitSpeakersIndex() {
		if (showMore) return speakerCount;
		else return maxSpeakers;
	}

	const visibleSpeakers = useMemo(() => page_module?.modules, [page_module?.modules]);

	const { custom_heading, description, title } = getTextValue(content, language);
	const customHeadingDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.secondaryBackgroundColor
		: ColorKeys.secondaryAccentColor;
	const textDefaultColor = (template === TemplateClassNames.O2)
		? ColorKeys.headingTextColor
		: ColorKeys.bodyTextColor;

	return page_module.is_on ? (
		<div
			ref={ref}
			className={classNames(
				"editor-wrapper speakers",
				`speakers-${layout_type}`,
				stylingOverrides.backgroundColor,
				template)}
		>
			<Controls
				openContentModal={openModal}
				module={page_module}
				contentButtonText={"Add speaker"}
				baseLanguage={baseLanguage}
				language={language}
			/>

			<div className="module-container" style={stylingOverrides.backgroundImg} >
				<EditableTextSlim
					tagType="p"
					className="speakers-custom_heading"
					value={custom_heading}
					defaultColor={customHeadingDefaultColor}
					onChange={handleTextChange("custom_heading")}
					onOverrideChange={handleStyleOverrideChange("custom_heading")}
					elementName={EContentColorOverrideNames.title}
					colorOverride={styling_overrides?.content?.custom_heading?.color}
				/>
				<EditableTextSlim
					tagType="h1"
					className="speakers-title"
					value={title}
					defaultColor={textDefaultColor}
					onChange={handleTextChange("title")}
					onOverrideChange={handleStyleOverrideChange("title")}
					elementName={EContentColorOverrideNames.title}
					colorOverride={styling_overrides?.content?.title?.color}
				/>
				<EditableTextDraft
					className="speakers-description"
					value={description}
					onChange={handleTextChange("description")}
					defaultColor={textDefaultColor}
					onOverrideChange={handleStyleOverrideChange("description")}
					elementName={EContentColorOverrideNames.description}
					colorOverride={styling_overrides?.content?.description?.color}
				/>

				{page_module.modules?.length ? (
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToParentElement]}
						autoScroll={false}
					>
						<SortableContext
							items={page_module.modules!.map((module: Speaker) => String(module.speaker))}
							strategy={rectSortingStrategy}
						>
							<ul className="speakers-list">
								{visibleSpeakers!
									.slice(0, limitSpeakersIndex())
									.map((speaker: Speaker, index: number) => (
										<SpeakerComponent
											speaker={speaker}
											key={index}
											id={String(speaker.speaker)}
											editSpeaker={editSpeaker}
											checkDeleteSpeaker={checkDeleteSpeaker}
											replaceSpeaker={replaceSpeaker}
										/>
									))}
							</ul>
						</SortableContext>
					</DndContext>
				)
					: <LargeButton
						title="Add Speaker"
						onClick={openModal}
						style={{ width: "100%", marginTop: '20px' }}
					/>
				}

				{speakerCount > maxSpeakers && (
					<div className="show-more-wrapper">
						<Button
							classButton={classNames(
								'secondary',
								{ "no-border": template === TemplateClassNames.O2 }
							)}
							template={template}
							onClick={handleShowMore}
						>
							{template == TemplateClassNames.Apollo && (
								<EIcon
									name="refresh-transparent"
									position="order-left"
									size="medium"
								/>
							)}
							Show {showMore ? 'Less' : 'More'}
						</Button>
					</div>
				)}

			</div>
			<CreateSpeakerModal
				language={language}
				baseLanguage={baseLanguage}
				editingSpeaker={workingSpeaker}
				open={modalOpen}
				close={closeModal}
				onComplete={onSpeakerComplete}
				onSelectionComplete={updatePageModule}
				initialSelected={page_module.modules}
				replacing={replacing}
				initialRelatedSessions={page_module.content?.related_sessions}
			/>
			<ModalComponent
				open={confirmingDelete}
				title={"Remove Speaker?"}
				onRequestClose={closeDeleteModal}
				cancellable
				closeable={false}
				footer={<>
					<button onClick={closeDeleteModal}>Cancel</button>
					<button className="primary" onClick={removeSpeaker}>Remove</button>
				</>}
			>
				<p>Are you sure you want to remove <strong>{speakerToRemove?.first_name} {speakerToRemove?.last_name}</strong>?</p>
			</ModalComponent>
		</div>
	) : null;
});

EditorSpeakers.displayName = "EditorSpeakers";

export default EditorSpeakers;